import type { Ceviri } from "../tip";
import type { sanal as tr } from "../tr/sanal";

const BIRIM: Readonly<Record<string, readonly [string, string]>> = {
  gun: ["day", "days"],
  hafta: ["week", "weeks"],
  ay: ["month", "months"],
};

/** English dictionary · sanal. */
export const sanal: Ceviri<typeof tr> = {
  sabitNot:
    "A virtual account rebuilds the past from its rule: past results do not predict the future; tax is not included; " +
    "funds that have closed or are no longer tracked are missing from the list, so broad selections may look better than they were.",

  ekran: {
    baslik: "Virtual Accounts",
    kisaAciklama: "Back-test.",
    aciklama:
      "Back-test: define a rule and the transactions are built from it with past prices. Nothing is written to your real vault.",
    derlemedeYok: "No virtual accounts in this build",
    derlemedeYokAciklama: "Open with the real device storage.",
    dosyadanAl: "Import from file",
    yeni: "New virtual account",
    varOlanlarSilinmedi: "Your existing virtual accounts were not deleted.",
    kuralKaydedilmedi: "The rule was not saved.",
    kaydedildiFiyatlar: (ad: string) => `"${ad}" saved. Fetching prices from the server…`,
    kaydedildiUyari: (ad: string, uyarilar: string) => `"${ad}" saved. Warning while fetching prices: ${uyarilar}`,
    kaydedildi: (ad: string) => `"${ad}" saved.`,
    paylasilanEklenmedi: "Shared rule not added",
    okunuyor: "Reading virtual accounts…",
    bosBaslik: "No virtual accounts yet",
    bosAciklama:
      "For example: had you started three years ago, buying 60% shares and 40% gold with the same amount every month, where would you be today?",
    liste: "Virtual accounts",
    bugun: "today",
    varlikDokumu: "Holdings",
    gunlukAnaliz: "Daily Analysis",
    duzenle: "Edit",
    kopyala: "Copy",
    paylasimUcretli: "Sharing is a paid feature (Subscription).",
    paylas: "Share",
    yenidenKaristir: "Reshuffle",
    silmeyiOnayla: "Confirm delete",
    vazgec: "Cancel",
    sil: "Delete",
  },

  renk: {
    mavi: "Blue",
    lacivert: "Navy",
    mor: "Purple",
    eflatun: "Lilac",
    turkuaz: "Turquoise",
    petrol: "Teal",
    kahverengi: "Brown",
    gri: "Grey",
  },

  form: {
    yeni: "New virtual account",
    duzenleEtiket: "Edit virtual account",
    duzenleBaslik: (ad: string) => `Edit: ${ad}`,
    ad: "Name",
    renk: "Colour",
    renksiz: "No colour",
    paraBirimi: "Account currency",
    paraBirimiIpucu: "Currency of the amounts paid in and withdrawn.",
    varliklar: "Assets and target weights",
    listeAlinamadi: "The server's asset list could not be fetched",
    listeAlinamadiMetin: "Only currencies can be chosen. Reopen the page once you are connected.",
    varlik: "Asset",
    oran: "Weight %",
    kusuratIpucu: "Can fractional units be bought",
    kusurat: "Fractional",
    kilitliIpucu: "Never sold when rebalancing by selling",
    kilitli: "Locked",
    cikar: "Remove",
    oranEtiket: (ad: string) => `${ad} weight (percent)`,
    kusuratEtiket: (ad: string) => `${ad} can be bought in fractions`,
    kilitliEtiket: (ad: string) => `${ad} locked`,
    toplam: "Total:",
    esitDagit: "Split evenly",
    varlikEkle: "Add asset",
    varlikEkleIpucu: "Search by symbol or name (the server's tracked list and currencies).",
    bulunanlar: "Assets found",
    baslangic: "Start",
    baslangicIpucu: "Every asset must have a price on this day.",
    bitis: "End (optional)",
    bitisIpucu: "If empty, the account runs up to today.",
    paraAkisi: "Cash flow",
    duzenliAlim: "Regular buying",
    defterAkisi: "Cash flow of my real accounts (same day, same amount)",
    baslangicTutari: "Starting amount (optional)",
    periyotTutari: "Buy amount per period",
    periyotTutariIpucu: "Never exceeded, commission included.",
    periyot: "Period",
    periyotBirimi: "Period unit",
    gunde: "per day",
    haftada: "per week",
    ayda: "per month",
    duzenliCekim: "Regular withdrawal (regular buying stops once it starts)",
    cekimTutari: "Withdrawal per period",
    cekimBaslangici: "Withdrawals start",
    defterAciklama:
      "Deposits and withdrawals in the selected accounts (or, if there are none, buy and sell amounts) are paid into the virtual account on the same day.",
    komisyon: "Commission (per transaction, on buys and sells)",
    komisyonAciklama:
      "Percentage + fixed amount, then a minimum / maximum. Example: a fixed $1.5 only; or 0.1%, minimum $0.75. Empty fields are not used.",
    yuzde: "Percent %",
    sabitTutar: "Fixed amount",
    enAz: "Minimum",
    enCok: "Maximum",
    tutarParasi: "Currency of the amounts",
    tutarParasiIpucu: "Converted to the asset's currency at the exchange rate of the trade date.",
    hesabinParasi: (para: string) => `Account currency (${para})`,
    kaymaGeri: "Buy-day shift: back (days)",
    kaymaIleri: "Forward (days)",
    kaymaIpucu: "Random each period; fixed by the seed.",
    temettu: "Dividend",
    ayniVarlik: "Reinvest in the same asset",
    sonrakiAlim: "Add to the next buy",
    yokSay: "Ignore (price return)",
    temettuKomisyonu: "Charge commission when reinvesting dividends too",
    artanSonrakiAlima: "Add cash left over from a buy (not enough for a unit or the commission) to the next buy",
    dengeleme: "Rebalancing",
    dengelemeYok: "None: same weights on every buy",
    dengelemeAlimla: "By buying: the most underweight first, as far as the cash goes",
    dengelemeSatarak: "By selling: sell what exceeds the threshold (locked assets excluded)",
    esik: "Rebalancing threshold (percentage points)",
    kaydet: "Save",
    farkliKaydet: "Save as new",
    vazgec: "Cancel",
  },

  hata: {
    ad: "Enter a name.",
    enAzBirVarlik: "Choose at least one asset.",
    enCokVarlik: (n: number) => `At most ${n} ${n === 1 ? "asset" : "assets"}.`,
    oranBicimi: "Enter weights as percentages (e.g. 40 or 33.33).",
    oranToplami: (toplam: string) => `Weights add up to ${toplam}%; they must total exactly 100%.`,
    oranSifir: "Every asset's weight must be greater than zero.",
    baslangic: "Choose a start date.",
    bitis: "The end cannot be before the start.",
    periyot: (enCok: number) => `A whole number from 1 to ${enCok}.`,
    tutar: "Enter a valid amount.",
    tutarYok: "Enter a starting amount or a per-period amount.",
    cekimSifir: "The withdrawal amount must be greater than zero.",
    cekimBaslangic: "Choose the date withdrawals start.",
    cekimOnce: "Withdrawals cannot start before the account does.",
    gercekHesap: "Choose at least one real account.",
    komisyon: "Enter the commission as a percentage (e.g. 0.2).",
    enCokEnAz: "The maximum cannot be less than the minimum.",
    esik: "Enter the threshold in percentage points (e.g. 3).",
    kayma: (enCok: number) => `Between 0 and ${enCok} days.`,
  },

  ozet: {
    duzenli: (adim: number, birim: string, tutar: string, para: string) => {
      const [tek, cok] = BIRIM[birim] ?? [birim, birim];
      return `${adim === 1 ? `every ${tek}` : `every ${adim} ${cok}`} ${tutar} ${para}`;
    },
    defter: "with the cash flow of real accounts",
    dengelemeYok: "no rebalancing",
    dengelemeAlimla: "rebalancing by buying",
    dengelemeSatarak: "rebalancing by selling",
    satir: (varlik: number, akis: string, dengeleme: string) =>
      `${varlik} ${varlik === 1 ? "asset" : "assets"} · ${akis} · ${dengeleme}`,
    temettuHaric: " · dividends excluded",
  },

  paylasim: {
    panelEtiket: (ad: string) => `Sharing ${ad}`,
    aciklama:
      "Shared: the rule (including the random seed), name and colour. The result is not shared; the recipient computes the same result on their own device. The rule sits after the “#” in the link and never reaches the server; the link does not expire.",
    defterNotu: " The real accounts that feed your cash flow are not shared.",
    link: "Share link",
    kopyala: "Copy link",
    qrGizle: "Hide QR",
    qrGoster: "Show QR",
    dosyaIndir: "Download as file",
    kopyalandi: "Link copied.",
    panoIzinYok: "The browser did not allow writing to the clipboard; select the link in the field and copy it.",
    qrEtiket: (ad: string) => `QR code of the ${ad} share link`,
    gelenBaslik: "Shared virtual account",
    zatenVar: (ad: string) => ` (“${ad}” already exists in your vault)`,
    bugun: "today",
    bBaslik: "Cash flow from your accounts",
    bMetin:
      "This rule's cash came from the sharer's real accounts; those accounts and their deposits are not shared. When adding, link the cash flow to your own accounts or choose a regular flow.",
    cihazinizda: "The result is computed on your device, with your own price archive.",
    eklenmedi: "This rule was not added to your vault.",
    ekleUc: "Add to virtual accounts…",
    ekle: "Add to virtual accounts",
    vazgec: "Cancel",
  },

  okuma: {
    yeniSurum: "This link is from a newer version of Parafon; update the app.",
    linkBozuk: "Broken link: the rule could not be read.",
    bicimDegil: (yer: string) => `The rule could not be read: ${yer} is not in the expected format.`,
    taninmayanAlan: (alan: string, yeniSurum: string) => `This link has an unrecognised field (${alan}). ${yeniSurum}`,
    eksik: (alan: string) => `The rule is incomplete: ${alan} is missing.`,
    metinOlmali: (yer: string) => `The rule could not be read: ${yer} must be text.`,
    tamSayiOlmali: (yer: string) => `The rule could not be read: ${yer} must be a whole number.`,
    bayrakOlmali: (yer: string) => `The rule could not be read: ${yer} must be true/false.`,
    taninmayanDeger: (yer: string, deger: string, yeniSurum: string) =>
      `This link has an unrecognised value (${yer}: ${deger}). ${yeniSurum}`,
    varliklarListe: "The rule could not be read: varliklar must be a list.",
    paylasimDegil: "This is not a Parafon virtual account share.",
    yerPaylasim: "share",
  },

  notlar: {
    bolum: "Virtual account",
    uretilemedi: "Transactions could not be generated",
    eksikler: "Gaps affecting the result",
    bekleyenNakit: "Cash left unused by buys:",
    gizle: "Hide transactions",
    goster: (n: number) => `Show ${n} ${n === 1 ? "transaction" : "transactions"} derived from the rule`,
    tabloBaslik: "Transactions of the virtual account (read-only)",
    tarih: "Date",
    tur: "Type",
    varlik: "Asset",
    miktar: "Quantity",
    fiyatTutar: "Price / amount",
    komisyon: "Commission",
    nakit: "Cash",
  },
};
